/* eslint-disable react/no-array-index-key */
import { IDatasetTableDefaultProps, IDatasetTotal } from "components/dashboard/dataset-table/utils/interfaces";
import { WIDTH_LOOKUP_TABLE } from "components/dashboard/dataset-table/utils/utils";
import { TitleOverrideAndDescriptionTemplate } from "components/templates/DataTableCellTemplates";
import {
  AdditionalDateColumnType,
  ComponentConfigDefault,
  DatasetDefaultConfig,
} from "models/dataset-models";
import React, { useState } from "react";
import { Instant } from "@js-joda/core";
import { Tooltip } from "react-tooltip";
import { useTimezone } from "hooks/useTimezone";
import { I, T } from "services/lang/LanguageService";
import { DweenTimeUnitEnum, fmtDateUntilUnit, toZonedDateTime } from "utils/time-utils";
import ColumnTabWithMapper from "./ColumnTabWithMapper";
import DatasetChartButtonWrapper, { TableTypes } from "./DatasetChartButtonWrapper";
import { getAdditionalDateColumn } from "../charts/utils";

const DatasetTableDefault = ({
  config,
  dateFrom,
  dateTo,
  data,
  datasetsArray,
  getCellStyle,
  totals,
  formatNumbers,
  additionalDateColumn,
  dayOfWeekDisplayType,
}: IDatasetTableDefaultProps) => {
  const tz = useTimezone();
  const showTotal = datasetsArray.some(obj => obj.showTotal);

  const dateBody = (date: string): string => {
    const dateFormat = config?.dateFormat || DweenTimeUnitEnum.MINUTES;
    return fmtDateUntilUnit(
      toZonedDateTime(date, tz.display),
      dateFormat,
      tz.display,
    );
  };

  const renderCellValue = (_key: string, value: string | number, index: number) => {
    const columnConfig = datasetsArray[index];
    const decimalPlaces = columnConfig.decimalPlaces ?? 2;

    if (columnConfig.stringValueMapper) {
      return <ColumnTabWithMapper value={value} mapperId={columnConfig.stringValueMapper} />;
    }

    if (!value && value !== 0) {
      return "-";
    }

    if (typeof value === "number") {
      const formattedValue = formatNumbers
        ? new Intl.NumberFormat("lt-LT", {
          minimumFractionDigits: decimalPlaces,
          maximumFractionDigits: decimalPlaces,
        }).format(value)
        : value.toFixed(decimalPlaces);
      return formattedValue;
    }

    return value;
  };

  const columnBody = data.map((obj, index) => {
    if (config?.transposed) {
      return <tr key={index} />;
    }
    return (
      <tr key={index}>
        {!config?.hideDateColumn && <td style={{ whiteSpace: "nowrap", padding: "2px 8px" }}>{dateBody(obj.date)}</td>}
        {additionalDateColumn && additionalDateColumn !== AdditionalDateColumnType.NONE && !config?.hideDateColumn && (
          <td style={{ whiteSpace: "nowrap", padding: "2px 8px", textAlign: "center" }}>
            {getAdditionalDateColumn(obj.date, additionalDateColumn, tz.display, dayOfWeekDisplayType)}
          </td>
        )}
        {Object.entries(obj).map(([key, value], idx) => {
          if (key !== "date") {
            return (
              <td key={idx} className="value-class" style={getCellStyle(key, value)}>
                {renderCellValue(key, value as string | number, idx)}
              </td>
            );
          }
          return null;
        })}
      </tr>
    );
  });

  const minWidthCalc = `calc(${datasetsArray.length + (config?.hideDateColumn ? 0 : 1)} * 100px)`;

  return (
    <div className="data-table-wrapper default-table">
      <table style={{ minWidth: minWidthCalc }}>
        <thead>
          <ColumnHeader
            config={config}
            datasetsArray={datasetsArray}
            dateFrom={dateFrom}
            dateTo={dateTo}
            additionalDateColumn={additionalDateColumn}
          />
        </thead>
        <tbody>{columnBody}</tbody>
        {showTotal && (
          <tfoot>
            <ColumnFooter totals={totals} />
          </tfoot>
        )}
      </table>
    </div>
  );
};

export default DatasetTableDefault;

const ColumnHeader: React.FC<{
  config?: ComponentConfigDefault;
  datasetsArray: DatasetDefaultConfig[];
  dateFrom?: Instant;
  dateTo?: Instant;
  additionalDateColumn?: AdditionalDateColumnType;
}> = ({ config, datasetsArray, dateFrom, dateTo, additionalDateColumn }) => {
  const [selectedIdentifiers, setSelectedIdentifiers] = useState<string[]>([]);

  return (
    <tr>
      {!config?.hideDateColumn && (
        <th
          style={{
            borderRight: "1px solid #d1d1d1",
            padding: "2px 8px",
            width: WIDTH_LOOKUP_TABLE[config?.dateFormat || DweenTimeUnitEnum.MINUTES],
          }}
        >
          {T("word.date")}
        </th>
      )}
      {additionalDateColumn && additionalDateColumn !== AdditionalDateColumnType.NONE && !config?.hideDateColumn && (
        <th
          style={{
            borderRight: "1px solid #d1d1d1",
            padding: "2px 0px",
            width: WIDTH_LOOKUP_TABLE[config?.dateFormat || DweenTimeUnitEnum.MINUTES],
          }}
        >
          {additionalDateColumn === AdditionalDateColumnType.WEEK ? T("word.week") : T("word.day-of-week")}
        </th>
      )}
      {datasetsArray?.map((col, i) => (
        <th key={i} style={{ padding: "2px 8px", borderLeft: "1px solid #d1d1d1" }}>
          <TitleOverrideAndDescriptionTemplate
            data={{
              description: col.description,
              title: col.title ?? "",
              overriddenTitle: col.overriddenTitle,
              unit: col.unit,
              identifier: col.identifier,
            }}
            version={2}
          />
          <DatasetChartButtonWrapper
            identifier={col.identifier}
            currentIdentifiers={selectedIdentifiers}
            setCurrentIdentifiers={setSelectedIdentifiers}
            tableType={TableTypes.DEFAULT}
            dateFrom={dateFrom}
            dateTo={dateTo}
          />
        </th>
      ))}
    </tr>
  );
};

const ColumnFooter = ({ totals }: { totals: IDatasetTotal[] }) => (
  <tr>
    <th scope="row" />
    {totals.length > 0 ? (
      totals.map(obj => (
        <td className="value-class" key={obj.identifier}>
          {obj.value ? (
            <>
              <Tooltip id="value-tooltip" opacity={0.7} place="top" float />
              <span data-tooltip-id="value-tooltip" data-tooltip-content={`${I(obj.tooltip ?? "")}`}>
                {obj.value}
              </span>
            </>
          ) : null}
        </td>
      ))
    ) : (
      <td className="value-class" />
    )}
  </tr>
);
